// Shared files (ClientSpace.dc.html): the files between the client and Adam, newest first, each
// downloaded through a link sign-file signs for 2 minutes, and "Add a file" ("Add a file for Laura"
// in Adam's view) through sign-upload straight into private storage. Only the client and an owner or
// admin at 2-step login can open or add them. While staff view as the client, nothing downloads or
// uploads.
import { useRef, useState } from 'react'
import { HubFunctionError } from '../../lib/functions.js'
import { readOnlyControl, VIEWING_AS_LINE } from '../../lib/viewAs.jsx'
import { FileIcon } from '../../product/Icons.jsx'
import { COPY } from './copy.js'
import { ACCEPT, MAX_BYTES, fileType, fileWhen, fileWho, typeAllowed } from './model.js'
import { signClientFile, uploadClientFile } from './data.js'

const FALLBACK = 'Something went wrong. Try again.'

export function FilesCard({ ctx }) {
  const { files, first, staff, open, viewingAs, space, patch, nowMs } = ctx
  const input = useRef(null)
  const [adding, setAdding] = useState('idle') // idle | busy | added
  const [msg, setMsg] = useState(null)
  const [rowMsg, setRowMsg] = useState({})
  const canAdd = open

  async function pick(e) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || adding === 'busy') return
    const type = fileType(file.name, file.type)
    if (!typeAllowed(type)) { setMsg(COPY.badType); return }
    if (file.size > MAX_BYTES) { setMsg(COPY.tooBig); return }
    if (!file.size) { setMsg(COPY.notAdded); return }
    setMsg(null)
    setAdding('busy')
    try {
      const row = await uploadClientFile(space.id, file, type)
      patch(d => ({ ...d, files: [row, ...d.files.filter(x => x.id !== row.id)] }))
      setAdding('added')
    } catch (err) {
      setMsg(err instanceof HubFunctionError ? err.message : COPY.notAdded)
      setAdding('idle')
    }
  }

  async function download(file) {
    if (viewingAs) { setRowMsg(m => ({ ...m, [file.id]: VIEWING_AS_LINE })); return }
    setRowMsg(m => ({ ...m, [file.id]: null }))
    try {
      const data = await signClientFile(file.id)
      if (typeof data?.url !== 'string' || !data.url.startsWith('https://')) throw new Error('no link')
      window.location.assign(data.url)
    } catch (err) {
      setRowMsg(m => ({ ...m, [file.id]: err instanceof HubFunctionError ? err.message : FALLBACK }))
    }
  }

  const label = adding === 'busy' ? COPY.adding : adding === 'added' ? COPY.added : staff ? COPY.addFileFor(first) : COPY.addFile

  return (
    <section className="ms-card" data-part="files" aria-labelledby="ms-files-h">
      <div className="ms-card-head ms-card-head--files">
        <h2 className="ms-h2 ms-h2--small" id="ms-files-h">{COPY.filesH2}</h2>
        {canAdd && (
          <>
            <button type="button" className="ms-btn-line ms-btn-line--small" disabled={adding === 'busy' || viewingAs}
              {...readOnlyControl(viewingAs)} onClick={() => input.current?.click()}>{label}</button>
            <input ref={input} type="file" accept={ACCEPT} className="ms-file-input" tabIndex={-1} aria-hidden="true" onChange={pick} />
          </>
        )}
      </div>
      {msg && <p className="ms-card-msg ms-card-msg--top" role="alert">{msg}</p>}
      {files.map(file => (
        <div key={file.id} className="ms-file">
          <span className="ms-file-icon"><FileIcon size={17} /></span>
          <span className="ms-file-text">
            <span className="ms-file-name">{file.file_name}</span>
            <span className="ms-file-meta">{COPY.fileFrom(fileWho(file, first), fileWhen(file, nowMs))}</span>
            {rowMsg[file.id] && <span className="ms-file-msg" role="alert">{rowMsg[file.id]}</span>}
          </span>
          <button type="button" className="ms-file-dl" aria-label={COPY.downloadAria(file.file_name)} onClick={() => download(file)}>
            {COPY.download}
          </button>
        </div>
      ))}
      {!files.length && <p className="ms-card-none">{COPY.noFiles}</p>}
      <p className="ms-card-foot ms-card-foot--files">{COPY.filesFoot}</p>
    </section>
  )
}
